// Uber Rides: a team member books an Uber for a driver or an owner in the Uber
// app and logs it here. db/migrations/5600.
//
// There is no approval: the person who books it logs it. Who carries the cost
// is chosen per ride (Fleetly, the driver or the car's owner), and anyone but
// Fleetly needs a reason. Recording it charges nobody yet. A driver is only
// charged through payroll's recovery policy, which needs a recoverable reason
// and the driver's written agreement (BCEA s34), and 'uber_ride' is not
// recoverable until Luyanda decides it is. An owner is only charged through the
// owner statement, which does not carry a ride line yet.
//
// Every figure and every refusal here is Postgres. CLAUDE.md rule 4.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Check, ChevronDown, ChevronRight, ExternalLink, Route, ShieldAlert, X } from 'lucide-react'
import { useCancelRide, useCorrectRideFare, useGetRideOptions, useGetRides } from '../hooks/backend/rides'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Chip } from '../components/interviews/Chip'
import { LogRideDialog } from '../components/rides/LogRideDialog'
import { formatWhen, formatZar } from '../utils/loans'
import { formatMonth } from '../utils/petrol'
import {
  CARRIED_BY_LABEL, CARRIED_BY_TONE, tripLink,
  type Ride, type RideList, type RideOptions,
} from '../utils/rides'

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'booked', label: 'Booked' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: '', label: 'All' },
]

const CARRIED_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Anyone carries it' },
  { value: 'fleetly', label: 'Fleetly carries it' },
  { value: 'driver', label: 'Driver carries it' },
  { value: 'owner', label: 'Owner carries it' },
]

export default function UberRides() {
  const optionsQuery = useGetRideOptions<RideOptions | null>()
  const listQuery = useGetRides<RideList | null>()
  const [params] = useSearchParams()
  // ?r= opens one ride, whatever its status.
  const deepLink = params.get('r')
  const [status, setStatus] = useState(deepLink ? '' : 'booked')
  const [carried, setCarried] = useState('')
  const [expanded, setExpanded] = useState<string | null>(deepLink)
  const [logging, setLogging] = useState(false)
  const [showSpend, setShowSpend] = useState(true)

  const loadOptions = optionsQuery.trigger
  const loadList = listQuery.trigger
  useEffect(() => { void loadOptions({}, { skipCache: true }).catch(() => undefined) }, [loadOptions])
  const refreshList = useCallback(() => {
    void loadList({ status: status || null, carried_by: carried || null }, { skipCache: true }).catch(() => undefined)
  }, [loadList, status, carried])
  useEffect(() => { refreshList() }, [refreshList])

  const options = optionsQuery.data ?? null
  const rows = listQuery.data?.rows ?? []
  const counts = listQuery.data?.counts
  const spend = listQuery.data?.spend ?? []
  const driverRecoverable = listQuery.data?.driver_recoverable === true

  if (optionsQuery.loading && !optionsQuery.data && !optionsQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>
  }
  if (optionsQuery.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{optionsQuery.error}</p>
          <Button size="sm" variant="outline" onClick={() => void loadOptions({}, { skipCache: true }).catch(() => undefined)}>Try again</Button>
        </div>
      </div>
    )
  }

  // Postgres answers null when this login may not log rides (uber_rides_allowed_v1).
  if (optionsQuery.data === null && !optionsQuery.loading) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">Uber rides are logged by Fleetly staff.</p>
          <p className="text-xs text-muted-foreground">If you are staff, ask an admin to switch on View for your login in cockpit access.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <Route className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Uber Rides</h1>
          <p className="text-xs text-muted-foreground">
            Rides booked for drivers and owners. Book it in the Uber app, then log it here with who carries the cost.
          </p>
        </div>
        {options?.can_log && (
          <Button variant="brand" size="sm" onClick={() => setLogging(true)}>
            <Route className="mr-1 h-4 w-4" /> Log a ride
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        {counts && (
          <>
            <Chip tone="neutral">{counts.this_month} this month, {formatZar(counts.this_month_zar)}</Chip>
            <Chip tone={counts.driver_borne ? 'amber' : 'neutral'}>{counts.driver_borne} on drivers, {formatZar(counts.driver_borne_zar)}</Chip>
            <Chip tone={counts.owner_borne ? 'brand' : 'neutral'}>{counts.owner_borne} on owners, {formatZar(counts.owner_borne_zar)}</Chip>
          </>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <NativeSelect className="h-8 w-36 text-xs" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </NativeSelect>
          <NativeSelect className="h-8 w-44 text-xs" value={carried} onChange={(e) => setCarried(e.target.value)} aria-label="Who carries it">
            {CARRIED_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </NativeSelect>
        </div>
      </div>

      {counts && (counts.driver_borne > 0 || counts.owner_borne > 0) && (
        <p className="border-b border-border bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
          Rides on drivers and owners are recorded, not charged.
          {driverRecoverable ? '' : ' Nothing is deducted from a driver until rides are made recoverable in pay and the driver agrees in writing.'}
          {' '}Nothing is on an owner statement yet.
        </p>
      )}

      {listQuery.error && <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{listQuery.error}</div>}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <section className="border-b border-border bg-muted/20">
          <button type="button" onClick={() => setShowSpend((v) => !v)} className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-medium text-foreground">
            {showSpend ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Ride spend by who carries it, last three months
          </button>
          {showSpend && (
            <div className="px-4 pb-3">
              {spend.length === 0 ? (
                <p className="text-xs text-muted-foreground">No ride has been logged in the last three months.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[18rem] text-xs">
                    <thead className="text-left text-muted-foreground">
                      <tr><th className="py-1 pr-3 font-medium">Month</th><th className="py-1 pr-3 font-medium">Carried by</th><th className="py-1 pr-3 text-right font-medium">Rides</th><th className="py-1 text-right font-medium">Fares</th></tr>
                    </thead>
                    <tbody>
                      {spend.map((s) => (
                        <tr key={`${s.month}-${s.carried_by}`} className="border-t border-border">
                          <td className="py-1 pr-3">{formatMonth(s.month)}</td>
                          <td className="py-1 pr-3">{CARRIED_BY_LABEL[s.carried_by]}</td>
                          <td className="py-1 pr-3 text-right tabular-nums">{s.rides}</td>
                          <td className="py-1 text-right font-medium tabular-nums">{formatZar(s.amount_zar)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>

        {listQuery.loading && !listQuery.data ? (
          <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading rides…</p>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {status === 'booked' && !carried ? 'No ride has been logged yet. Log one with the button above once it is booked in the Uber app.' : 'No ride matches these filters.'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <RideItem key={r.id} ride={r} open={expanded === r.id}
                onToggle={() => setExpanded((x) => (x === r.id ? null : r.id))} onChanged={refreshList} />
            ))}
          </ul>
        )}
      </div>

      <LogRideDialog open={logging} onOpenChange={setLogging} options={options} onSaved={refreshList} />
    </div>
  )
}

function RideItem({ ride: r, open, onToggle, onChanged }: {
  ride: Ride; open: boolean; onToggle: () => void; onChanged: () => void
}) {
  const fare = useCorrectRideFare()
  const cancel = useCancelRide()
  const [error, setError] = useState<string | null>(null)
  const [correcting, setCorrecting] = useState(false)
  const [newFare, setNewFare] = useState('')
  const [fareNote, setFareNote] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const busy = fare.loading || cancel.loading
  const link = tripLink(r.uber_trip_ref)

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try { await fn(); onChanged() } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  return (
    <li>
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-accent/40">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="w-16 shrink-0 font-mono text-xs text-muted-foreground">{r.ref}</span>
        <span className="min-w-[10rem] flex-1">
          <span className="block text-sm font-medium text-foreground">
            {r.rider_name ?? '—'} <span className="text-xs font-normal text-muted-foreground">({r.rider_kind})</span>
          </span>
          <span className="block text-xs text-muted-foreground">{r.reason_label ?? r.reason_kind}, {formatWhen(r.ride_at)}</span>
        </span>
        <span className={`w-24 text-right text-sm font-semibold tabular-nums ${r.status === 'cancelled' ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{formatZar(r.amount_zar)}</span>
        <span className="flex w-full justify-end gap-1 sm:w-auto">
          {r.status === 'cancelled'
            ? <Chip tone="neutral">Cancelled</Chip>
            : <Chip tone={CARRIED_BY_TONE[r.carried_by]}>{CARRIED_BY_LABEL[r.carried_by]} carries it</Chip>}
        </span>
      </button>

      {open && (
        <div className="space-y-3 bg-muted/20 px-4 pb-4 pl-11 text-sm">
          <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <Row k="Why" v={`${r.reason_label ?? r.reason_kind}: ${r.reason}`} />
            <Row k="From" v={r.pickup} />
            <Row k="To" v={r.dropoff} />
            <Row k="When" v={formatWhen(r.ride_at)} />
            <Row k="Car" v={r.vehicle ?? '—'} />
            <Row k="Fare" v={r.first_amount_zar != null
              ? `${formatZar(r.amount_zar)}, corrected from ${formatZar(r.first_amount_zar)}${r.fare_corrected_by_name ? ` by ${r.fare_corrected_by_name}` : ''}: ${r.fare_note ?? ''}`
              : formatZar(r.amount_zar)} />
            <Row k="Carried by" v={r.carried_by === 'owner'
              ? `The owner, ${r.charge_owner_name ?? '—'}: ${r.carried_by_note ?? ''}`
              : r.carried_by === 'driver' ? `The driver: ${r.carried_by_note ?? ''}` : 'Fleetly'} />
            <Row k="Logged by" v={`${r.booked_by_name ?? '—'}, ${formatWhen(r.booked_at)}`} />
            {r.uber_trip_ref && !link && <Row k="Uber trip" v={r.uber_trip_ref} />}
            {r.cancelled_at && <Row k="Cancelled" v={`${formatWhen(r.cancelled_at)}${r.cancelled_by_name ? ` by ${r.cancelled_by_name}` : ''}: ${r.cancel_reason ?? ''}`} />}
          </dl>

          {link && (
            <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--brand))] hover:underline">
              <ExternalLink className="h-3.5 w-3.5" /> Open the trip in Uber
            </a>
          )}

          {r.can_change && (
            <div className="flex flex-wrap items-center gap-2">
              {!correcting && !cancelling && (
                <>
                  <Button size="sm" variant="outline" onClick={() => { setNewFare(Number(r.amount_zar).toFixed(2)); setCorrecting(true) }}>Correct the fare</Button>
                  <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>Cancel this ride</Button>
                </>
              )}
              {correcting && (
                <span className="flex flex-wrap items-center gap-1">
                  <Input className="h-8 w-28 text-xs" inputMode="decimal" aria-label="Final fare" value={newFare} onChange={(e) => setNewFare(e.target.value)} />
                  <Input className="h-8 w-64 text-xs" placeholder="Why, e.g. final charge on the Uber receipt" value={fareNote} onChange={(e) => setFareNote(e.target.value)} />
                  <Button size="sm" variant="brand" disabled={busy}
                    onClick={() => void run(async () => { await fare.trigger({ id: r.id, amount_zar: newFare, note: fareNote }); setCorrecting(false); setFareNote('') })}>
                    {fare.loading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Check className="mr-1 h-3.5 w-3.5" />} Save the fare
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setCorrecting(false)}><X className="h-3.5 w-3.5" /></Button>
                </span>
              )}
              {cancelling && (
                <span className="flex flex-wrap items-center gap-1">
                  <Input className="h-8 w-64 text-xs" placeholder="Why it is being cancelled" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                  <Button size="sm" variant="destructive" disabled={busy}
                    onClick={() => void run(async () => { await cancel.trigger({ id: r.id, reason: cancelReason }); setCancelling(false) })}>
                    Cancel it
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setCancelling(false)}><X className="h-3.5 w-3.5" /></Button>
                </span>
              )}
            </div>
          )}
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
        </div>
      )}
    </li>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-foreground">{v}</dd>
    </div>
  )
}
